import React, { useCallback, useState } from "react";
import Chart from "react-apexcharts";
import { toast } from "react-toastify";
import { useDropzone } from "react-dropzone";
import OverlayLoading from "../components/OverlayLoading";
import Title from "../components/title";
import DropzoneComponent from "../components/Utils/DropzoneComponent";
import { colors } from "../global";
import { apiFetch } from "../apiClient";

const TicketPerformance = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dashboardData, setDashboardData] = useState({});

  const onDrop = useCallback(async (acceptedFiles) => {
    setIsSubmitting(true);

    const convertFile = async (file) => {
      if (!file) return;
      try {
        const formData = new FormData();
        formData.append("file", file);

        const response = await apiFetch(`/api/v1/ticketPerformance/upload`, {
          method: "POST",
          body: formData,
          credentials: "include",
        });

        if (!response.ok) {
          let errorMessage = "Upload failed.";
          try {
            const errorResponse = await response.json();
            errorMessage =
              errorResponse.detail || errorResponse.error || errorMessage;
          } catch (err) {
            console.error("Failed to parse error response", err);
          }
          toast.error(errorMessage);
          return;
        }

        const result = await response.json();
        setDashboardData(result);
      } catch (err) {
        toast.error("Upload failed. Please try again.");
      } finally {
        setIsSubmitting(false);
      }
    };

    if (acceptedFiles.length === 0) return setIsSubmitting(false);
    await convertFile(acceptedFiles[0]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ".csv",
    multiple: false,
  });

  const colorPalette = [
    colors.primary,
    colors.secondary,
    colors.accent,
    colors.darkGray,
    colors.info,
    colors.warning,
    colors.error,
    colors.lightAccent,
    colors.mutedBlue,
    colors.success,
  ];

  return (
    <>
      {isSubmitting && (
        <OverlayLoading
          isVisible={isSubmitting}
          message="Processing your request..."
          icon="truck"
        />
      )}

      <div className="p-6 pb-0">
        <Title title={"Ticket Performance"} />
      </div>

      <div className="m-4 p-6 bg-white rounded-xl border border-gray-100 shadow-sm">
        <DropzoneComponent
          getInputProps={getInputProps}
          getRootProps={getRootProps}
          isDragActive={isDragActive}
          fileTypes="CSV"
        />
      </div>

      {dashboardData?.stackedChart?.categories?.length > 0 && (
        <div className="p-4 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white p-4 rounded-lg shadow col-span-2">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">
                Time Breakdown Per Owner
              </h2>
            </div>
            <Chart
              key={JSON.stringify(dashboardData.stackedChart)}
              options={{
                chart: { type: "bar", stacked: true, stackType: "100%" },
                plotOptions: { bar: { horizontal: true, borderRadius: 6 } },
                xaxis: { categories: dashboardData.stackedChart.categories },
                stroke: { width: 1, colors: ["#fff"] },
                fill: { opacity: 1 },
                colors: colorPalette,
                legend: { position: "top", horizontalAlign: "left" },
                tooltip: {
                  y: {
                    formatter: (val) => `${val} Minutes`,
                  },
                },
              }}
              series={dashboardData.stackedChart.series}
              type="bar"
              height={250}
            />
          </div>
          <div className="bg-white p-4 rounded-lg shadow col-span-2">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">
                Time Breakdown Per Ticket
              </h2>
            </div>
            <Chart
              key={JSON.stringify(dashboardData.timeSpentPerTicketChart)}
              options={{
                chart: { type: "bar", stacked: true, stackType: "100%" },
                plotOptions: { bar: { horizontal: true, borderRadius: 6 } },
                xaxis: {
                  categories: dashboardData.timeSpentPerTicketChart.categories,
                },
                stroke: { width: 1, colors: ["#fff"] },
                fill: { opacity: 1 },
                colors: colorPalette,
                legend: { position: "top", horizontalAlign: "left" },
                tooltip: {
                  y: {
                    formatter: (val) => `${val} Minutes`,
                  },
                },
              }}
              series={dashboardData.timeSpentPerTicketChart.series}
              type="bar"
              height={800}
            />
          </div>

          <div
            className="bg-white p-4 rounded-lg shadow col-span-2"
            style={{
              width: "100%",
              overflowX: "auto",
              overflowY: "hidden",
              padding: "1rem",
            }}
          >
            <div
              style={{
                minWidth: `${
                  dashboardData.comparePerTicket.categories.length * 350
                }px`,
              }}
            >
              <Chart
                key={JSON.stringify(dashboardData.comparePerTicket)}
                options={{
                  chart: {
                    type: "bar",
                    height: 700,
                    toolbar: {
                      show: true,
                      tools: {
                        download: true,
                        selection: true,
                        zoom: true,
                        zoomin: true,
                        zoomout: true,
                        pan: true,
                        reset: true,
                        customIcons: [],
                      },
                      autoSelected: "zoom",
                    },
                  },
                  plotOptions: {
                    bar: {
                      horizontal: false,
                      columnWidth: "40%",
                      endingShape: "rounded",
                      grouped: true,
                      borderRadius: "5",
                    },
                  },
                  stroke: {
                    show: true,
                    width: 1,
                    colors: ["#ccc"],
                  },
                  xaxis: {
                    categories: dashboardData.comparePerTicket.categories,
                    tooltip: { enabled: true },
                    labels: {
                      rotate: -30,
                      style: { fontSize: "12px" },
                      trim: true,
                      hideOverlappingLabels: false,
                    },
                  },
                  yaxis: {
                    min: -1,
                  },
                  dataLabels: {
                    enabled: true,
                    formatter: (val) => (val === 0 ? "0" : val),
                    style: {
                      fontSize: "10px",
                      colors: ["#333"],
                    },
                  },
                  legend: { position: "top" },
                  tooltip: {
                    y: { formatter: (val) => `${val} Minutes` },
                  },
                  colors: colorPalette,
                }}
                series={dashboardData.comparePerTicket.series}
                type="bar"
                height={700}
              />
            </div>
          </div>
          <div className="col-span-2 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {dashboardData.radarSpentTimeByOwner &&
              Object.entries(dashboardData.radarSpentTimeByOwner).map(
                ([owner, radar], idx) => (
                  <div key={idx} className="bg-white p-4 rounded-lg shadow">
                    <h2 className="text-lg font-semibold mb-2">
                      {owner}'s Time Allocation
                    </h2>
                    <Chart
                      key={JSON.stringify(radar)}
                      options={{
                        series: radar.series,
                        chart: {
                          height: 350,
                          type: "radar",
                        },
                        yaxis: {
                          stepSize: 20,
                        },
                        xaxis: {
                          categories: radar.categories,
                        },
                        tooltip: {
                          y: {
                            formatter: (val) => `${val} Minutes`,
                          },
                        },
                      }}
                      series={radar.series}
                      type="radar"
                      height={400}
                    />
                  </div>
                )
              )}
          </div>

          <div className="bg-white p-4 rounded-lg shadow">
            <h2 className="text-lg font-semibold mb-4">Time Spent by Owner</h2>
            <Chart
              key={JSON.stringify(dashboardData.timeByOwner)}
              options={{
                chart: { type: "bar" },
                xaxis: { categories: dashboardData.timeByOwner.categories },
                colors: [colors.primary],
                plotOptions: { bar: { horizontal: true, borderRadius: 6 } },
                tooltip: {
                  y: {
                    formatter: (val) => `${val} Minutes`,
                  },
                },
              }}
              series={dashboardData.timeByOwner.series}
              type="bar"
              height={400}
            />
          </div>

          <div className="bg-white p-4 rounded-lg shadow">
            <h2 className="text-lg font-semibold mb-4">
              Ticket Status Distribution
            </h2>
            <Chart
              key={JSON.stringify(dashboardData.statusDistribution)}
              options={{
                chart: { type: "bar" },
                xaxis: {
                  categories: dashboardData.statusDistribution.categories,
                },
                colors: [colors.primary],
                plotOptions: { bar: { horizontal: true, borderRadius: 6 } },
                tooltip: {
                  y: {
                    formatter: (val) =>
                      `${val} ${val > 1 ? "Tickets" : "Ticket"}`,
                  },
                },
              }}
              series={dashboardData.statusDistribution.series}
              type="bar"
              height={300}
            />
          </div>

          <div className="bg-white p-4 rounded-lg shadow">
            <h2 className="text-lg font-semibold mb-4">
              Time Allocation by Activity
            </h2>
            <Chart
              key={JSON.stringify(dashboardData.timeAllocation)}
              options={{
                chart: { type: "donut" },
                labels: dashboardData.timeAllocation.labels,
                colors: colorPalette,
                legend: { position: "bottom" },
              }}
              series={dashboardData.timeAllocation.series}
              type="donut"
              height={400}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default TicketPerformance;
